import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '@callmart/types';
import { useQuery } from '@tanstack/react-query';
import { ChannelOptions } from '../../enums/ChannelOptions';
import { usePreferredUserLocation, useChannels } from '../../hooks';
import UserContext from '../../store/User/UserContext';
import { getAdditionalWorktypeChannelForLocation } from '../../services/additionalLocationDeltaWorktypeChannel';
import { QueryKey } from '../../enums/QueryKey';
import DownloadExcelButton from '../Buttons/DownloadExcelButton';
import { getAllDatesInAMonth, getAllHourSlotsInATimeRange } from '../../utils/dateUtils';
import { isUserHasLocation } from '../../utils/authUtils';
import { getWorkingUnitShortName } from '../../utils/workingUnitUtils';
import dayjs from '@utils/dayjs';

const columnNames = [
    'START_TIME',
    'HOUR',
    'MINUTE',
    'Calldelta',
    'Stadt',
    'Standort Detail',
    'Worktype',
    'Kanal',
    'Grund',
];

function LocationDeltaEmptyTemplateDownloadButton({ selectedDate, selectedLocation, allWorktypes }) {
    const { t } = useTranslation('allDeltas');
    const loggedInUser = useContext(UserContext);

    const isUserAllowedToAccessLocation = useMemo(
        () => isUserHasLocation(loggedInUser, selectedLocation),
        [loggedInUser, selectedLocation]
    );

    const { data: additionalWorktypeChannelsForLocation } = useQuery({
        queryKey: [QueryKey.AdditionalWorktypeChannelsForLocation, { location: selectedLocation }],
        queryFn: () => getAdditionalWorktypeChannelForLocation(selectedLocation),
        initialData: [],
        enabled: isUserAllowedToAccessLocation && !!selectedLocation,
    });

    const [location] = usePreferredUserLocation(selectedLocation);
    const [channels] = useChannels();

    const worktypes = useMemo(() => {
        const channelShortNames = {};
        for (const ch of channels) {
            channelShortNames[ch.name] = ch.shortName || ch.name;
        }

        return [
            ...allWorktypes.map((worktype) => ({
                ...worktype,
                worktypeName: worktype.worktype,
                sheetName: getWorkingUnitShortName({
                    worktype: { shortName: worktype.shortName, worktype: worktype.worktype },
                    channel: {
                        shortName: channelShortNames[ChannelOptions.INBOUND_VOICE.name],
                        name: ChannelOptions.INBOUND_VOICE.name,
                    },
                }),
                channel: ChannelOptions.INBOUND_VOICE.name,
            })),
            ...additionalWorktypeChannelsForLocation.map((worktypeChannel) => ({
                worktypeName: worktypeChannel.worktype.worktype,
                sheetName: getWorkingUnitShortName({
                    worktype: {
                        shortName: worktypeChannel.worktype.shortName,
                        worktype: worktypeChannel.worktype.worktype,
                    },
                    channel: {
                        shortName: worktypeChannel.channel.shortName || channelShortNames[worktypeChannel.channel.name],
                        name: worktypeChannel.channel.name,
                    },
                }),
                channel: worktypeChannel.channel.name,
            })),
        ];
    }, [allWorktypes, additionalWorktypeChannelsForLocation, channels]);

    const getXLSXSheets = (worktypes, currentMonthDates, allHourSlots) => {
        const sheetsData = [];
        for (const worktype of worktypes) {
            const sheetData = [];

            for (const date of currentMonthDates) {
                for (const hourSlot of allHourSlots) {
                    const start_time = date.format(DATE_FORMATS.DATE);
                    const hour = hourSlot.split(':')[0];
                    const minute = hourSlot.split(':')[1];
                    const row = [
                        start_time,
                        hour,
                        minute,
                        '',
                        location?.city,
                        location?.code,
                        worktype.worktypeName,
                        worktype.channel,
                        '',
                    ];

                    sheetData.push(row);
                }
            }

            sheetsData.push({
                sheetName: worktype.sheetName,
                sheetData: sheetData,
            });
        }

        return sheetsData;
    };

    return (
        <DownloadExcelButton
            title="Standortdelta Template"
            fileName={`delta_${location?.city}_${dayjs(selectedDate).format(DATE_FORMATS.MONTH_YEAR_UNDERSCORE)}_template.xlsx`}
            location={selectedLocation}
            columnNames={columnNames}
            getSheetsData={() =>
                getXLSXSheets(
                    worktypes,
                    getAllDatesInAMonth(selectedDate),
                    getAllHourSlotsInATimeRange({ startTime: 7, endTime: 22 })
                )
            }
            buttonText={t('template.emptyButton')}
        ></DownloadExcelButton>
    );
}

export default LocationDeltaEmptyTemplateDownloadButton;
